import { FC } from 'react';
import { Statistic, StatisticProps } from 'antd';
import { TbSquareRounded } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import {
    useReportData,
    useReportQuery,
} from '@/shared/components/modals/ResourceActionModals/EditReportComponentModal/helpers/hook';
import { ReportComponent } from '@/types';
import { StatisticConfig } from '@/types/api/modules/business-intelligence';

import { useNivoReportFormatting } from '../../nivo/useNivoReportFormatting';

interface SingleStatisticProps {
    component: ReportComponent;
}

const SingleStatistic: FC<SingleStatisticProps> = ({ component }): JSX.Element => {
    const { token } = useAntDesignTheme();
    const config = component.configuration as Partial<StatisticConfig> | undefined;
    const query = useReportQuery(component);
    const { data } = useReportData(component);
    const { formatMetricValue } = useNivoReportFormatting(component);

    const metricField = query.metrics[0]?.field;
    const rawValue = metricField ? data.rows[0]?.[metricField] : config?.value;
    const value = typeof rawValue === 'number' ? rawValue : Number(rawValue ?? 0);
    const displayValue = formatMetricValue(Number.isFinite(value) ? value : 0);
    const formatter: StatisticProps['formatter'] = (nextValue) => {
        return displayValue || Number(nextValue).toLocaleString();
    };

    return (
        <div
            style={{
                alignItems: 'center',
                display: 'flex',
                height: '100%',
                justifyContent: 'center',
                minHeight: 0,
                padding: '1rem',
                textAlign: 'center',
                containerType: 'inline-size',
                width: '100%',
            }}
        >
            <Statistic
                prefix={config?.prefix}
                suffix={config?.suffix}
                formatter={formatter}
                valueRender={(node) => (
                    <span
                        style={{
                            alignItems: 'center',
                            display: 'inline-flex',
                            maxWidth: '100%',
                            minWidth: 0,
                            whiteSpace: 'nowrap',
                        }}
                    >
                        <TbSquareRounded
                            size='1rem'
                            style={{ flex: '0 0 auto', marginRight: '0.5rem' }}
                            stroke={token.blue5}
                            fill={token.blue2}
                        />
                        <span
                            title={String(displayValue)}
                            style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}
                        >
                            {node}
                        </span>
                    </span>
                )}
                value={Number.isFinite(value) ? value : 0}
                style={{ maxWidth: '100%', width: '100%' }}
                valueStyle={{ fontSize: 'clamp(1rem, 8cqw, 1.875rem)', lineHeight: 1.25, whiteSpace: 'nowrap' }}
            />
        </div>
    );
};

export default SingleStatistic;
